









import { defineComponent } from "@vue/composition-api";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { CallPrice } from "@scrile/api-provider/dist/api/CallPriceProvider";
import { MessagePrices } from "@scrile/api-provider/dist/api/MessagePricesProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { mixinReroute } from "@/lib/decorators";
import config from "@/config";
import { FormValues } from "@/types";
import PriceSettingsView from "./components/PriceSettingsView.vue";

export default defineComponent({
  name: "PagePriceSettings",
  components: { PriceSettingsView },
  mixins: [mixinReroute(config.featureCalling || config.featurePaidSentMessage)],
  setup() {
    const { authUserStrict } = useAuthUser();
    return {
      authUserStrict,
    };
  },
  data() {
    return {
      loading: false as boolean,
      callPrice: null as CallPrice | null,
      messagePrice: null as MessagePrices | null,
      callOptions: null as CallOptions | null,
      callPaymentPackages: [] as PaymentPackage[],
      messagePaymentPackages: [] as PaymentPackage[],
      values: {
        callPricePackageId: null,
        incomingCallsEnabled: true,
        messagePricePackageId: "0",
        perSentWithAttachmentPriceId: "0",
        subscriberPerSentPriceId: "0",
        subscriberPerSentWithAttachmentPriceId: "0",
        freeMessageLimit: 0,
      } as FormValues,
    };
  },
  computed: {
    checkedCallOptionsAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyCallOptions"]);
    },
    checkedCallAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyCallPrice"]);
    },
    checkedMessageAuthorities(): boolean {
      return checkAuthorities(this.$store.state?.authorities || [], ["updateMyMessagePrice"]);
    },
  },
  methods: {
    async getCallPrice() {
      if (config.featureCalling && this.checkedCallAuthorities) {
        this.callPrice = (await providers.CallPriceProvider.find({ userIds: [this.authUserStrict.id] }))[0] ?? null;
        this.values = { ...this.values, callPricePackageId: this.callPrice?.perMinutePriceId || "0" };
      }
    },
    async getMessagePrice() {
      if (config.featurePaidSentMessage && this.checkedMessageAuthorities) {
        this.messagePrice =
          (await providers.MessagePricesProvider.find({ userIds: [this.authUserStrict.id] }))[0] ?? null;
        this.values = {
          ...this.values,
          messagePricePackageId: this.messagePrice?.perSentPriceId || "0",
          perSentWithAttachmentPriceId: this.messagePrice?.perSentWithAttachmentPriceId || "0",
          subscriberPerSentPriceId: this.messagePrice?.subscriberPerSentPriceId || "0",
          subscriberPerSentWithAttachmentPriceId: this.messagePrice?.subscriberPerSentWithAttachmentPriceId || "0",
          freeMessageLimit: this.messagePrice?.freeMessageLimit ?? 0,
        };
      }
    },
    async getPaymentPackages() {
      const packageTypes = [];
      if (config.featureCalling && (this.checkedCallAuthorities || this.checkedCallOptionsAuthorities)) {
        packageTypes.push(PaymentPackageEnum.PER_MINUTE_CALL);
      }
      if (config.featurePaidSentMessage && this.checkedMessageAuthorities) {
        packageTypes.push(PaymentPackageEnum.PER_SENT_MESSAGE);
      }
      if (packageTypes.length === 0) return;
      const paymentPackages = await providers.PaymentPackageProvider.findAll({ type: packageTypes });
      const callPaymentPackages: PaymentPackage[] = [];
      const messagePaymentPackages: PaymentPackage[] = [];
      paymentPackages.forEach(paymentPackage => {
        if (paymentPackage.type === PaymentPackageEnum.PER_MINUTE_CALL) {
          callPaymentPackages.push(paymentPackage);
        }
        if (paymentPackage.type === PaymentPackageEnum.PER_SENT_MESSAGE) {
          messagePaymentPackages.push(paymentPackage);
        }
      });
      this.callPaymentPackages = callPaymentPackages;
      this.messagePaymentPackages = messagePaymentPackages;
    },
    async getCallOptions() {
      if (config.featureCalling && this.checkedCallOptionsAuthorities) {
        this.callOptions =
          (await providers.CallOptionsProvider.find({ userIds: [this.authUserStrict.id] })).find(
            i => i.userId === this.authUserStrict.id
          ) ?? null;
        this.values = { ...this.values, incomingCallsEnabled: this.callOptions?.enabled ?? true };
      }
    },
    async onSubmit(cb: Function) {
      try {
        await Promise.all([this.updateCallPrice(), this.updateMessagePrice(), this.updateCallOptions()]);
        await this.$router.back();
      } finally {
        cb();
      }
    },
    async updateCallPrice() {
      if (config.featureCalling && this.checkedCallAuthorities && this.values.incomingCallsEnabled) {
        this.callPrice = await providers.CallPriceProvider.update({
          data: { userId: this.authUserStrict.id, perMinutePriceId: this.values.callPricePackageId || "0" },
        });
      }
    },
    async updateMessagePrice() {
      if (config.featurePaidSentMessage && this.checkedMessageAuthorities) {
        this.messagePrice = await providers.MessagePricesProvider.update({
          data: {
            userId: this.authUserStrict.id,
            perSentPriceId: this.values.messagePricePackageId || "0",
            perSentWithAttachmentPriceId: this.values.perSentWithAttachmentPriceId || "0",
            subscriberPerSentPriceId: this.values.subscriberPerSentPriceId || "0",
            subscriberPerSentWithAttachmentPriceId: this.values.subscriberPerSentWithAttachmentPriceId || "0",
            freeMessageLimit: this.values.freeMessageLimit ?? 0,
          },
        });
      }
    },
    async updateCallOptions() {
      if (config.featureCalling && this.checkedCallOptionsAuthorities) {
        const data = { userId: this.authUserStrict.id, enabled: this.values.incomingCallsEnabled };
        this.callOptions = await providers.CallOptionsProvider.update({ data });
      }
    },
  },
  async created() {
    if (!this.checkedCallAuthorities && !this.checkedMessageAuthorities && !this.checkedCallOptionsAuthorities) {
      await this.$router.replace({ name: "home" });
    } else {
      try {
        this.loading = true;
        await Promise.all([
          this.getCallPrice(),
          this.getCallOptions(),
          this.getPaymentPackages(),
          this.getMessagePrice(),
        ]);
      } finally {
        this.loading = false;
      }
    }
  },
});
